import {useCallback, useEffect, useState} from 'react';

import type Registry from 'src/internal/Registry';
import type {CommonValues, Extra} from 'src/internal/types';

export default function useLazyX<TValues extends {[key: string]: unknown}>(
  registry: Registry<TValues>,
  values: TValues,
  fallback: CommonValues<TValues>,
): <TName extends keyof TValues>(
  name: TName,
  extra?: Extra,
) => TValues[TName] | CommonValues<TValues> {
  const [unique] = useState(() => Symbol('x'));

  useEffect(() => {
    return () => {
      registry.delete(unique);
    };
  }, [registry, unique]);

  return useCallback(
    <TName extends keyof TValues>(name: TName, extra?: Extra) => {
      const has = typeof name === 'string' && values?.hasOwnProperty(name);
      const valueRaw = values?.[name];
      const valueWithFallback = has ? valueRaw ?? fallback : fallback;
      const value = valueWithFallback as TValues[TName];

      registry.add(name, value, extra, unique);

      return value;
    },
    [unique, values, fallback, registry],
  );
}
